import { useCallback, useEffect, useRef, useState } from "react";
import { generateDescription } from "../api/aiDescriptionApi";
import {
  AI_TYPES,
  buildAIFields,
  missingAIFields,
  missingFieldsMessage,
  normalizeGenerated,
  redactContacts,
  stripHtml,
  toHtml,
} from "../utils/aiDescription";

const MIN_INTERVAL_MS = 2000;
export const DEFAULT_MAX_LENGTH = 2000;

/**
 * State machine behind <AIDescriptionAssist/>.
 *  - values: the raw form values for the field map of `type`
 *  - value / onChange: the controlled description (plain text, or HTML when format === "html")
 */
const useAIDescription = ({ type, values, value, onChange, maxLength = DEFAULT_MAX_LENGTH, format = "plain" }) => {
  const [status, setStatus] = useState("idle"); // idle | loading | error
  const [error, setError] = useState(null);
  const [tone, setTone] = useState("Professional");
  const [language, setLanguage] = useState("English");
  const [canUndo, setCanUndo] = useState(false);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const controllerRef = useRef(null);
  const previousRef = useRef(null);
  const lastAiTextRef = useRef("");
  const lastCallRef = useRef(0);
  const lastActionRef = useRef("generate");
  const busyRef = useRef(false);

  useEffect(() => () => controllerRef.current?.abort(), []);

  const fields = buildAIFields(type, values);
  const missing = missingAIFields(type, fields);
  const missingMessage = missingFieldsMessage(missing);
  const currentText = format === "html" ? stripHtml(value) : String(value ?? "").trim();

  const run = useCallback(
    async (action) => {
      if (busyRef.current || missing.length) return;
      const now = Date.now();
      if (now - lastCallRef.current < MIN_INTERVAL_MS) return;
      lastCallRef.current = now;

      busyRef.current = true;
      lastActionRef.current = action;
      setConfirming(false);
      setError(null);
      setStatus("loading");
      const controller = new AbortController();
      controllerRef.current = controller;

      try {
        const usesExisting = action !== "generate" && action !== "regenerate";
        const generated = await generateDescription(
          {
            type,
            fields,
            tone,
            language,
            maxLength,
            wordRange: AI_TYPES[type].wordRange,
            action,
            existingText: usesExisting ? redactContacts(currentText) : undefined,
          },
          { signal: controller.signal }
        );
        const text = normalizeGenerated(generated, { maxLength, format });
        if (!text) {
          setError({ code: "empty", message: "The AI returned an empty description. Please try again." });
          setStatus("error");
          return;
        }
        previousRef.current = value ?? "";
        lastAiTextRef.current = text;
        onChange(format === "html" ? toHtml(text) : text);
        setCanUndo(true);
        setHasGenerated(true);
        setStatus("idle");
      } catch (err) {
        if (err?.code === "aborted") {
          setStatus("idle");
        } else {
          // The user's existing text is never touched on failure
          setError({ code: err?.code || "server", message: err?.message || "Couldn't generate a description." });
          setStatus("error");
        }
      } finally {
        busyRef.current = false;
        controllerRef.current = null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, JSON.stringify(fields), tone, language, maxLength, format, currentText, value, onChange, missing.length]
  );

  /** First click: confirm before replacing text the user wrote themselves. */
  const generate = useCallback(() => {
    const userWroteIt = currentText && currentText !== lastAiTextRef.current;
    if (userWroteIt) {
      setConfirming(true);
      return;
    }
    run(hasGenerated ? "regenerate" : "generate");
  }, [currentText, hasGenerated, run]);

  const cancel = useCallback(() => controllerRef.current?.abort(), []);
  const dismissConfirm = useCallback(() => setConfirming(false), []);
  const retry = useCallback(() => run(lastActionRef.current), [run]);

  const undo = useCallback(() => {
    if (previousRef.current === null) return;
    onChange(previousRef.current);
    previousRef.current = null;
    lastAiTextRef.current = "";
    setCanUndo(false);
  }, [onChange]);

  return {
    status,
    error,
    tone,
    setTone,
    language,
    setLanguage,
    canUndo,
    hasGenerated,
    confirming,
    missing,
    missingMessage,
    currentText,
    generate,
    replaceWithNew: () => run(hasGenerated ? "regenerate" : "generate"),
    improveExisting: () => run("improve"),
    shorter: () => run("shorter"),
    longer: () => run("longer"),
    regenerate: () => run("regenerate"),
    retry,
    cancel,
    undo,
    dismissConfirm,
  };
};

export default useAIDescription;
